import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { THEME } from '../constants/theme';
import { useAnalytics } from '../hooks/useAnalytics';

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectUri = searchParams.get('redirect_uri');
  const { logEvent } = useAnalytics();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const { onClose } = location.state || {};

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleRedirectToApp = (session) => {
    if (redirectUri) {
      // Construct the redirect URL with the tokens
      const redirectUrl = new URL(redirectUri);
      redirectUrl.searchParams.set('access_token', session.access_token);
      redirectUrl.searchParams.set('refresh_token', session.refresh_token);
      
      console.log('Redirecting to mobile app:', redirectUrl.toString());
      window.location.replace(redirectUrl.toString());
    } else {
      navigate('/onboarding');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setLoading(true);

    try {
      logEvent('Signup', 'Signup Attempt', 'Email Signup');
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: redirectUri 
            ? `${window.location.origin}/auth/callback?redirect_uri=${encodeURIComponent(redirectUri)}`
            : `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      if (data?.session) {
        handleRedirectToApp(data.session);
      } else {
        // Email confirmation required
        navigate('/verify-email');
      }
      logEvent('Signup', 'Signup Success', 'Email Signup');

      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(err.message);
      logEvent('Signup', 'Signup Error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      logEvent('Signup', 'Signup Attempt', 'Google Signup');
      // ... existing Google signup logic ...
      logEvent('Signup', 'Signup Success', 'Google Signup');
    } catch (error) {
      logEvent('Signup', 'Signup Error', error.message);
    }
  };

  const handlePlanSelection = (plan) => {
    logEvent('Signup', 'Plan Selection', plan);
    // ... existing plan selection logic ...
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden">
      {/* Background Circles */}
      <div className="absolute left-0 bottom-0 w-96 h-96 bg-[#E5F5F5] rounded-full -translate-x-1/2 translate-y-1/2" />
      <div className="absolute right-0 top-0 w-96 h-96 bg-[#E5F5F5] rounded-full translate-x-1/2 -translate-y-1/2" />
      <div className="absolute right-0 top-0 w-16 h-16 text-[#E5F5F5]">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.0001 1H13.0001V4H11.0001V1ZM15.0001 2H17.0001V5H15.0001V2ZM7.00006 3H9.00006V6H7.00006V3ZM20.0001 4H22.0001V7H20.0001V4ZM3.00006 5H5.00006V8H3.00006V5Z" />
        </svg>
      </div>

      <div className="w-full max-w-md px-6 relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <img src="/logo.png" alt="Aided" className="h-12" />
        </div>

        {/* Sign Up Form */}
        <div className="bg-[#004D4D] rounded-2xl p-8 shadow-lg">
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-full p-4">
              <svg className="w-8 h-8 text-[#004D4D]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                required
              />
            </div>

            <div>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Password"
                className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                required
              />
            </div>

            <div>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm Password"
                className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                required
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            <div className="flex justify-between items-center text-sm">
              <Link 
                to={"/login" + (redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : '')}
                state={{ onClose }}
                className="font-medium hover:text-primary-dark"
                style={{ color: THEME.primary }}
              >
                Already have an account?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#66B2B2] hover:bg-[#99CCCC] text-white rounded-lg px-4 py-3 font-medium transition-colors relative"
            >
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                'Sign Up'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
} 